import type { PlatformDashboard } from '../../api/m19';

export const DASHBOARD_KEY = ['platform', 'dashboard'] as const;

export type TenantBucket = 'active' | 'trial' | 'grace' | 'locked' | 'suspended';

/** Exclusive tenant buckets in display order, with their meaning colour (labels always shown). */
export const BUCKETS: { key: TenantBucket; label: string; color: string; hint: string }[] = [
  { key: 'active', label: 'Active', color: 'var(--mui-palette-success-main)', hint: 'Paid' },
  { key: 'trial', label: 'Trial', color: 'var(--mui-palette-primary-main)', hint: 'Never paid' },
  { key: 'grace', label: 'Grace', color: 'var(--mui-palette-warning-main)', hint: 'Past due' },
  { key: 'locked', label: 'Locked', color: 'var(--mui-palette-error-main)', hint: 'Grace over' },
  {
    key: 'suspended',
    label: 'Suspended',
    color: 'var(--mui-palette-text-secondary)',
    hint: 'By the platform',
  },
];

/** Share of each bucket in percent (0 when there are no tenants). */
export function bucketShares(t: PlatformDashboard['tenants']): Record<TenantBucket, number> {
  const total = BUCKETS.reduce((s, b) => s + t[b.key], 0);
  const pct = (n: number) => (total === 0 ? 0 : (n / total) * 100);
  return {
    active: pct(t.active),
    trial: pct(t.trial),
    grace: pct(t.grace),
    locked: pct(t.locked),
    suspended: pct(t.suspended),
  };
}

/** "21 Oct" for a UTC calendar date `2026-10-21`. */
export function shortDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** Calendar days (admin's local time) from `now` until `iso`; negative when past. */
export function daysUntil(iso: string, now = Date.now()): number {
  const day = (t: number) => {
    const d = new Date(t);
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  };
  return Math.round((day(new Date(iso).getTime()) - day(now)) / 86_400_000);
}

/** "Expires today", "in 3 days", "2 days ago". */
export function expiryLabel(iso: string, now = Date.now()): string {
  const d = daysUntil(iso, now);
  if (d === 0) return 'Ends today';
  if (d === 1) return 'Ends tomorrow';
  if (d > 1) return `Ends in ${String(d)} days`;
  return d === -1 ? 'Ended yesterday' : `Ended ${String(-d)} days ago`;
}

/** Percent of issued codes that were redeemed (null when none were issued). */
export function redemptionRate(c: PlatformDashboard['codes30d']): number | null {
  return c.issued === 0 ? null : Math.round((c.redeemed / c.issued) * 100);
}

const ACTION_LABELS: Record<string, string> = {
  'platform.login': 'Signed in',
  'platform.logout': 'Signed out',
  'platform.mfa_challenge': 'Passed a two-step challenge',
  'platform.mfa_setup': 'Started two-step setup',
  'platform.mfa_enable': 'Turned on two-step verification',
  'platform.mfa_disable': 'Turned off two-step verification',
  'platform.password_change': 'Changed password',
  'platform.refresh_reuse_detected': 'Session token reuse detected',
  'platform_admin.create': 'Added a platform admin',
  'platform_admin.update': 'Updated a platform admin',
  'platform_admin.reset_mfa': 'Reset an admin’s two-step verification',
  'permission.denied': 'Was denied access',
  'tenant.view': 'Viewed a tenant',
  'tenant.menus': 'Changed a tenant’s menus',
  'tenant.backup': 'Downloaded a tenant backup',
  'tenant.user_password_reset': 'Reset a tenant user’s password',
  'tenant.suspend': 'Suspended a tenant',
  'tenant.resume': 'Resumed a tenant',
  'subscription.update': 'Updated a subscription',
  'subscription.extend': 'Extended a subscription',
  'activation_code.generate': 'Issued an activation code',
  'activation_code.revoke': 'Revoked an activation code',
  'activation_code.deliver': 'Delivered an activation code',
  'plan.create': 'Created a plan',
  'plan.update': 'Updated a plan',
  'support.access': 'Opened support data',
};

/** A readable label for a platform audit action: known ones mapped, others humanized. */
export function auditLabel(action: string): string {
  const known = ACTION_LABELS[action];
  if (known) return known;
  const s = action
    .replace(/^platform\./, '')
    .replace(/[._]/g, ' ')
    .trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "Good morning" etc. for the admin's local time. */
export function greeting(date = new Date()): string {
  const h = date.getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

/** "Oct 26" for a UTC month key `2026-10`. */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, 1)));
}

/** Share of new shops that paid over the period, in percent (null when no shops were added). */
export function conversionRate(rows: PlatformDashboard['conversions']): number | null {
  const started = rows.reduce((n, r) => n + r.trialsStarted, 0);
  const converted = rows.reduce((n, r) => n + r.converted, 0);
  return started === 0 ? null : Math.min(100, Math.round((converted / started) * 100));
}

const SLICE_COLORS = [
  'var(--viz-1)',
  'var(--viz-2)',
  'var(--viz-3)',
  'var(--viz-4)',
  'var(--viz-5)',
];

/**
 * Donut slices for the plan mix: the four largest plans keep their own colour (fixed order by
 * size), the rest fold into "Other" so no generated hue is ever needed.
 */
export function planSlices(
  mix: PlatformDashboard['planMix'],
): { id: string; label: string; value: number; color: string }[] {
  const top = mix.slice(0, mix.length > 5 ? 4 : 5);
  const rest = mix.slice(top.length);
  const slices = top.map((p, i) => ({
    id: p.planId ?? 'none',
    label: p.name,
    value: p.shops,
    color: SLICE_COLORS[i] ?? 'var(--viz-5)',
  }));
  if (rest.length)
    slices.push({
      id: 'other',
      label: `Other (${String(rest.length)} plans)`,
      value: rest.reduce((n, p) => n + p.shops, 0),
      color: 'var(--mui-palette-text-disabled)',
    });
  return slices;
}
